import type { ComponentProps } from 'react';
import { isEmpty } from 'lodash';

import ClaimManagementTab from '~/components/ClaimManagementTab';
import ClaimMilestonesTab from '~/components/ClaimMilestones/ClaimMilestonesTab';
import { CliveInsightsTabLabel } from '~/components/ClaimPage/CliveApp/CliveInsights/CliveInsightsTabLabel';
import ClaimSummary from '~/components/ClaimSummary';
import { CLIVE_AVATARS } from '~/components/Clive/cliveRegistry';
import CommunicationsTab from '~/components/communications/CommunicationsTab';
import ContactsTab from '~/components/ContactsTab';
import DocumentsScreen from '~/components/Documents/DocumentsScreen';
import FinancesScreen from '~/components/Finances/FinancesScreen';
import GalleryScreen from '~/components/Gallery/GalleryScreen';
import type { IconProps } from '~/components/icons/types';
import { LegalActionsTab } from '~/components/LegalActions/LegalActionsTab/LegalActionsTab';
import { LegalActionsTabLabel } from '~/components/LegalActions/LegalActionsTab/LegalActionsTabLabel';
import type { LegalActionApiResponse } from '~/components/LegalActions/types';
import NotesScreen from '~/components/Notes/NotesScreen';
import PolicyScreen from '~/components/PolicyScreen';
import RelatedClaimsTab from '~/components/RelatedClaimsTab';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import type { OrganizationModel } from '~/components/types/organization-types';
import Osha301Screen from '~/components/WC/WCOsha';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, isLegacyOrgFeatureEnabled } from '~/Utils';
import { isReinsuranceManagementClaim } from '~/Utils/ClaimUtils';

import ClaimNotificationsTab from '../../CliveApp/ClaimNotificationsTab';
import CliveChat from '../../CliveApp/CliveChat';
import CliveInsights from '../../CliveApp/CliveInsights';
import ConductorHub from '../../CliveApp/ConductorHub';
import { sidebarFieldKeyToIconComponent } from '../../constants';
import type { ClaimPermissions, ClaimSidebarFieldConfiguration } from '../../types';

export type SideBarKey = ClaimSidebarFieldConfiguration['field_key'];

export type UseClaimSideBarTabsArgs = {
  userOrganization: OrganizationModel;
  claim?: ClaimPolymorph;
  hasLegalActionsPermissions: boolean;
  legalActions: LegalActionApiResponse[];
  reloadLegalActions: () => Promise<void>;
  isClaimChatEnabled: boolean;
  isCliveInsightsTabEnabled: boolean;
  undismissedCount: number;
  isConductorHubShown: boolean;
};

type TabKeysToComponentMap = {
  summary: typeof ClaimSummary;
  management: typeof ClaimManagementTab;
  notes: typeof NotesScreen;
  legal_actions: typeof LegalActionsTab;
  finances: typeof FinancesScreen;
  communications: typeof CommunicationsTab;
  contacts: typeof ContactsTab;
  documents: typeof DocumentsScreen;
  gallery: typeof GalleryScreen;
  policy: typeof PolicyScreen;
  osha: typeof Osha301Screen;
  related_claims: typeof RelatedClaimsTab;
  notifications: typeof ClaimNotificationsTab;
  clive_chat: typeof CliveChat;
  clive_insights: typeof CliveInsights;
  conductor_hub: typeof ConductorHub;
  milestones: typeof ClaimMilestonesTab;
};

/** The tabs whose label is a component rather than a static string, and the component each uses. */
type TabKeysToLabelComponentMap = {
  legal_actions: typeof LegalActionsTabLabel;
  clive_insights: typeof CliveInsightsTabLabel;
};

export type ClaimSideBarTab<Key extends SideBarKey> = {
  label: string;
  url: Key;
  component: TabKeysToComponentMap[Key];
  LabelComponent?: Key extends keyof TabKeysToLabelComponentMap ? TabKeysToLabelComponentMap[Key] : undefined;
  IconComponent: React.ComponentType<IconProps>;
  IconProps?: IconProps;
  shouldDisplay: boolean;
  preLoad?: boolean;
  persist?: boolean;
  componentProps?: Partial<ComponentProps<TabKeysToComponentMap[Key]>>;
  labelComponentProps?: Key extends keyof TabKeysToLabelComponentMap
    ? ComponentProps<TabKeysToLabelComponentMap[Key]>
    : never;
  shouldReloadLegalActions?: boolean;
  permissions?: ClaimPermissions;
  shouldDisplayEmbeddedChat?: boolean;
};

export const isSidebarTabEnabledForOrganization = (tabKey: SideBarKey, organization: OrganizationModel): boolean => {
  if (tabKey === 'legal_actions') {
    return isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.LEGAL_ACTIONS);
  }
  if (tabKey === 'related_claims') {
    return isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.SHOW_RELATED_CLAIMS_TAB);
  }
  if (tabKey === 'osha') {
    return isLegacyOrgFeatureEnabled(organization, LEGACY_ORG_FEATURES.MGM_RW_OSHA_REPORTS);
  }
  if (tabKey === 'notifications') {
    return isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CLIVE_APP_CLAIM_PAGE);
  }
  if (tabKey === 'conductor_hub') {
    return isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CONDUCTOR_HUB_CLAIM_TAB);
  }
  if (tabKey === 'milestones') {
    return isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.CLAIM_MILESTONES);
  }
  return true;
};

const getDocumentsTabLabel = (claim: UseClaimSideBarTabsArgs['claim']): string => {
  const documents = claim?.documents?.filter((document) => !document?.is_removed) ?? [];
  return isEmpty(documents) ? 'Documents' : `Documents (${documents.length})`;
};

export const tabKeyToTabGetter: { [Key in SideBarKey]: (args: UseClaimSideBarTabsArgs) => ClaimSideBarTab<Key> } = {
  summary: ({ userOrganization }) => ({
    label: 'Summary',
    url: 'summary',
    component: ClaimSummary,
    IconComponent: sidebarFieldKeyToIconComponent.summary,
    shouldDisplay: isSidebarTabEnabledForOrganization('summary', userOrganization),
    preLoad: true,
    persist: true,
    shouldReloadLegalActions: true,
    shouldDisplayEmbeddedChat: true,
  }),

  management: ({ claim, userOrganization }) => ({
    label: `Management (${claim?.exposures?.length})`,
    url: 'management',
    component: ClaimManagementTab,
    IconComponent: sidebarFieldKeyToIconComponent.management,
    shouldDisplay: isSidebarTabEnabledForOrganization('management', userOrganization),
    preLoad: true,
    persist: true,
    shouldReloadLegalActions: true,
    shouldDisplayEmbeddedChat: true,
  }),

  notes: ({ userOrganization }) => ({
    label: 'Notes',
    url: 'notes',
    component: NotesScreen,
    IconComponent: sidebarFieldKeyToIconComponent.notes,
    shouldDisplay: isSidebarTabEnabledForOrganization('notes', userOrganization),
    preLoad: true,
    persist: true,
    shouldDisplayEmbeddedChat: true,
  }),

  legal_actions: ({ hasLegalActionsPermissions, legalActions, reloadLegalActions, userOrganization }) => ({
    label: 'Legal Actions',
    LabelComponent: LegalActionsTabLabel,
    url: 'legal_actions',
    component: LegalActionsTab,
    IconComponent: sidebarFieldKeyToIconComponent.legal_actions,
    shouldDisplay: isSidebarTabEnabledForOrganization('legal_actions', userOrganization) && hasLegalActionsPermissions,
    labelComponentProps: { legalActions },
    componentProps: { onNewLegalAction: reloadLegalActions },
    shouldReloadLegalActions: true,
    shouldDisplayEmbeddedChat: true,
  }),

  finances: ({ userOrganization, claim }) => ({
    label: 'Finances',
    url: 'finances',
    component: FinancesScreen,
    IconComponent: sidebarFieldKeyToIconComponent.finances,
    shouldDisplay:
      isSidebarTabEnabledForOrganization('finances', userOrganization) &&
      (!claim || !isReinsuranceManagementClaim(claim, userOrganization)),
    persist: true,
    shouldDisplayEmbeddedChat: true,
  }),

  communications: ({ userOrganization }) => ({
    label: 'Communications',
    url: 'communications',
    component: CommunicationsTab,
    IconComponent: sidebarFieldKeyToIconComponent.communications,
    shouldDisplay: isSidebarTabEnabledForOrganization('communications', userOrganization),
    preLoad: true,
    persist: true,
    shouldDisplayEmbeddedChat: true,
  }),

  contacts: ({ userOrganization }) => ({
    label: 'Contacts',
    url: 'contacts',
    component: ContactsTab,
    IconComponent: sidebarFieldKeyToIconComponent.contacts,
    shouldDisplay: isSidebarTabEnabledForOrganization('contacts', userOrganization),
    shouldDisplayEmbeddedChat: true,
  }),

  documents: ({ claim, userOrganization }) => ({
    label: getDocumentsTabLabel(claim),
    url: 'documents',
    component: DocumentsScreen,
    IconComponent: sidebarFieldKeyToIconComponent.documents,
    shouldDisplay: isSidebarTabEnabledForOrganization('documents', userOrganization),
    shouldDisplayEmbeddedChat: true,
  }),

  gallery: ({ userOrganization }) => ({
    label: 'Gallery',
    url: 'gallery',
    component: GalleryScreen,
    IconComponent: sidebarFieldKeyToIconComponent.gallery,
    shouldDisplay: isSidebarTabEnabledForOrganization('gallery', userOrganization),
    shouldDisplayEmbeddedChat: true,
  }),

  policy: ({ userOrganization }) => ({
    label: 'Policy',
    url: 'policy',
    component: PolicyScreen,
    IconComponent: sidebarFieldKeyToIconComponent.policy,
    shouldDisplay: isSidebarTabEnabledForOrganization('policy', userOrganization),
    persist: true,
    shouldDisplayEmbeddedChat: true,
  }),

  related_claims: ({ userOrganization, claim }) => ({
    label: 'Related Claims',
    url: 'related_claims',
    component: RelatedClaimsTab,
    IconComponent: sidebarFieldKeyToIconComponent.related_claims,
    shouldDisplay: isSidebarTabEnabledForOrganization('related_claims', userOrganization),
    componentProps: { claimId: claim?.id },
    shouldDisplayEmbeddedChat: true,
  }),

  osha: ({ claim, userOrganization }) => ({
    label: 'OSHA form',
    url: 'osha',
    component: Osha301Screen,
    IconComponent: sidebarFieldKeyToIconComponent.osha,
    shouldDisplay: isSidebarTabEnabledForOrganization('osha', userOrganization) && claim?.type === 'wc_claim',
    shouldDisplayEmbeddedChat: true,
  }),

  notifications: ({ userOrganization }) => ({
    label: 'Notifications',
    url: 'notifications',
    component: ClaimNotificationsTab,
    IconComponent: sidebarFieldKeyToIconComponent.notifications,
    shouldDisplay: isSidebarTabEnabledForOrganization('notifications', userOrganization),
    shouldDisplayEmbeddedChat: true,
  }),

  clive_chat: ({ isClaimChatEnabled }) => ({
    label: 'Ask Clive',
    url: 'clive_chat',
    component: CliveChat,
    IconComponent: CLIVE_AVATARS.happy,
    shouldDisplay: isClaimChatEnabled,
    shouldDisplayEmbeddedChat: false,
  }),

  clive_insights: ({
    userOrganization,
    isCliveInsightsTabEnabled,
    isClaimChatEnabled,
    undismissedCount,
    isConductorHubShown,
  }) => ({
    label: 'Clive Insights',
    LabelComponent: CliveInsightsTabLabel,
    labelComponentProps: { undismissedCount },
    url: 'clive_insights',
    component: CliveInsights,
    componentProps: { areActionsInConductorHub: isConductorHubShown },
    IconComponent: sidebarFieldKeyToIconComponent.clive_insights,
    IconProps: {
      size: 22,
      className: 'mb-4',
    },
    // With its actions in Automation First, only the insights are left for this tab to show.
    shouldDisplay:
      isCliveInsightsTabEnabled &&
      isClaimChatEnabled &&
      (!isConductorHubShown ||
        isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.AI_SERVICE_UI_INSIGHTS_TAB_DISPLAY)),
    shouldDisplayEmbeddedChat: true,
  }),

  conductor_hub: ({ userOrganization, isClaimChatEnabled }) => ({
    label: 'Automation First',
    url: 'conductor_hub',
    component: ConductorHub,
    IconComponent: sidebarFieldKeyToIconComponent.conductor_hub,
    // `isClaimChatEnabled` for the same reason `clive_insights` takes it: both read
    // `/claims/{id}/actions`, which 403s through `abort_if_chat_is_not_available`, and the token
    // route is behind the `CLIVE_CHAT` permission too.
    shouldDisplay: isSidebarTabEnabledForOrganization('conductor_hub', userOrganization) && isClaimChatEnabled,
    shouldDisplayEmbeddedChat: false,
  }),

  milestones: ({ userOrganization }) => ({
    label: 'Milestones',
    url: 'milestones',
    component: ClaimMilestonesTab,
    IconComponent: sidebarFieldKeyToIconComponent.milestones,
    shouldDisplay: isSidebarTabEnabledForOrganization('milestones', userOrganization),
    shouldDisplayEmbeddedChat: true,
  }),
};

/** The claim's sidebar tabs, in `fields` order, keeping only those that display. */
export const getClaimSideBarTabs = (
  fields: { field_key: SideBarKey }[],
  args: Omit<UseClaimSideBarTabsArgs, 'isConductorHubShown'>
): ClaimSideBarTab<SideBarKey>[] => {
  const isConductorHubShown =
    fields.some(({ field_key }) => field_key === 'conductor_hub') &&
    tabKeyToTabGetter.conductor_hub({ ...args, isConductorHubShown: false }).shouldDisplay;

  // `fields` is server-configured, so a key this build has no getter for is skipped, not called.
  return fields
    .filter(({ field_key }) => Object.hasOwn(tabKeyToTabGetter, field_key))
    .map(({ field_key }) => tabKeyToTabGetter[field_key]({ ...args, isConductorHubShown }))
    .filter((tab) => tab.shouldDisplay);
};
